import { BidderRule } from '@pubstack/common/src/bidder-rule'
import { Site } from '@pubstack/common/src/site'
import { StackContext } from '@pubstack/common/src/stackContext'
import { AdmStackOutput, isAdmStack, isPbsStack, PbsStack, PbsStackOutput, Stack } from '@pubstack/common/src/stack'
import { useQueryClient } from '@tanstack/react-query'
import { openJsonInNewTab } from '~/utils/fileDownload'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, useCallback, useMemo } from 'react'
import { STACKS_QUERY_KEYS, toBulkUpdate, useBulkUpdateStacks } from '~/api/stacks.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { useToast } from '~/components/Toast/useToasts'
import { Product } from '~/hooks/useProduct'
import { CreateUpdatedVersionModal } from './CreateUpdatedVersionModal'
import { AbDeployModal } from './AbDeployModal'
import { EditStackFlyout } from './EditStackFlyout'
import { Colors } from '~/assets/style/colors'
import { ViewStackFlyout } from './ViewStackFlyout'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { cn } from '@pubstack/ui/utils/cn'
import TableCell from '~/components/table/TableCell'
import TableRow, { ROW_CELL_CLASSES } from '~/components/table/TableRow'
import { admOutputOf, serversideQualifiersOf, serversideRoutesOf } from './admServerside'
import { IconWithTooltip } from '~/components/IconWithTooltip'
import { StackStatus } from './StackStatus'
import { Qualifier } from '~/components/Qualifier'
import Button from '~/components/Button'
import { ActionButtons, ActionsTableCell } from '~/components/_styled/table'
import { Tooltip } from '~/components/Tooltip'
import { isStackNameReserved } from '@pubstack/common/src/stack'
import { UnarchiveStackModal } from '~/modules/pbs/sites/site/stacks/UnarchiveStackModal'
import { useNavigate } from 'react-router-dom'
import { configurationState } from '~/state'
import { useAtomValue } from 'jotai'

type StackTableRowProps = {
  product: Product
  site: Site
  stack: Stack
  concurrentStack: Stack | undefined
  isFirst: boolean
  isLast: boolean
  bidderRules: BidderRule[]
  showStackConfigColumn?: boolean
  contexts: StackContext[]
  isEditingPriorities: boolean
  maxPriority: number
  updatePriority: (stackGroupId: string, priority: -1 | 1) => void
  onStackArchive: (stack: Stack, concurrentStack?: Stack) => Promise<void>
}

const ConfigButtonsCell = ({ stackConfigUrl, stack }: { stackConfigUrl: string; stack: Stack }) => {
  const devices = ['desktop', 'mobile'] as const
  const serverside = admOutputOf(stack)?.serverside

  return (
    <TableCell>
      {isAdmStack(stack) && (
        <>
          {devices.map((device) => {
            return (
              <a key={device} href={`${stackConfigUrl}/${stack.stackGroupId}/${stack.version}/${device}.json`} download target={'_blank'} rel={'noreferrer'}>
                <Button
                  variant={'tertiary'}
                  iconName={device}
                  onClick={(event) => {
                    event.stopPropagation()
                  }}
                  trackingLabel={`adm-sites-stacks-${device}-config-download`}
                />
              </a>
            )
          })}
          {/* Not on the CDN, unlike the device configs: prebid-server-proxy reads it from the database, so the
              stored JSON is opened the way the Node button does. Always drawn beside them, disabled when the stack
              has none, so the three configs of a One stack keep their places from row to row. */}
          <Tooltip title={serverside ? 'Server-side config' : 'No server-side config: the rule selects no Node, Spark or Spark for Pubs seat this site maps'}>
            <Button
              variant={'tertiary'}
              iconName={'server'}
              disabled={!serverside}
              onClick={(event) => {
                event.stopPropagation()
                if (serverside) openJsonInNewTab(serverside)
              }}
              trackingLabel={'adm-sites-stacks-serverside-config-download'}
            />
          </Tooltip>
        </>
      )}
      {isPbsStack(stack) && (
        <Button
          variant={'tertiary'}
          iconName={'desktop'}
          onClick={(event) => {
            event.stopPropagation()
            openJsonInNewTab(stack.output ?? ({} as PbsStackOutput))
          }}
          trackingLabel={`pbs-sites-stacks-config-download`}
        />
      )}
    </TableCell>
  )
}

export const StackTableRow: FunctionComponent<StackTableRowProps> = ({
  product,
  site,
  stack,
  concurrentStack,
  isFirst,
  isLast,
  bidderRules,
  showStackConfigColumn,
  contexts,
  isEditingPriorities,
  maxPriority,
  updatePriority,
  onStackArchive,
}) => {
  // Tracking labels
  const EDIT_STACK_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-edit`
  const VIEW_STACK_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-view`
  const DEPLOY_STACK_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-deploy-apply`
  // const AB_DEPLOY_STACK_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-ab-deploy-apply`
  const EDIT_AB_DEPLOY_RATIO_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-ab-deploy-ratio-edit`
  const ARCHIVE_STACK_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-archive`
  const UNARCHIVE_STACK_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-unarchive`

  // API Hooks
  const { mutateAsync: bulkUpdateStacks, isPending: isUpdatePending } = useBulkUpdateStacks()

  // Hooks
  const modal = useGlobalModal()
  const { canEdit, disabledReason } = useScopePermissions()
  const flyout = useGlobalFlyout()
  const toast = useToast()
  const queryClient = useQueryClient()
  const user = useAuthenticatedUser()
  const navigate = useNavigate()
  const { STACK_CONFIG_URL_PREFIX } = useAtomValue(configurationState)

  const bidderRuleName = useMemo(() => bidderRules.find((rule) => rule.id === stack.configuration.bidderRuleId)?.name, [bidderRules, stack])
  const noBiddersMapped = useMemo(() => {
    if (stack.type === 'pbs' && isEmpty((stack.output as PbsStackOutput)?.adUnits)) return true
    if (stack.type === 'adm' && (isEmpty((stack.output as AdmStackOutput)?.desktop) || isEmpty((stack.output as AdmStackOutput)?.mobile))) return true
    return false
  }, [stack])
  const serversideRoutes = useMemo(() => serversideRoutesOf(stack), [stack])
  const isArchived = useMemo(() => stack.status === 'archived_group', [stack.status])
  const priority = useMemo(() => {
    // Only the first row of a stack group (stackGroupId) shows a priority; other versions below stay blank.
    if (!isFirst) {
      return ''
    }
    if (isArchived) {
      return '-'
    }
    if (isStackNameReserved(stack.name)) {
      return maxPriority + 1
    }
    return stack.priority
  }, [isArchived, stack, maxPriority, isFirst])

  const getStackConfigUrlPrefix = () => {
    const shortScopeId = stack.scopeId.split('-')[0]
    const shortSiteId = site.id.split('-')[0]
    const suffix = `${shortScopeId}/${shortSiteId}`
    return `${STACK_CONFIG_URL_PREFIX}/${suffix}`
  }

  const handleCreateUpdatedVersion = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (isAdmStack(stack) || isPbsStack(stack)) {
        modal.open(CreateUpdatedVersionModal, { stack, concurrentStack })
      }
    },
    [modal, stack, concurrentStack]
  )

  const handleDeployClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    modal.open(AbDeployModal, {
      site,
      stack,
      concurrentStack,
    })
  }

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (stack.type === 'pbs') {
      flyout.open(EditStackFlyout, { color: Colors.King, closeTrackingLabel: `${EDIT_STACK_TRACKING_LABEL}-close` }, { mode: 'edit', siteId: site.id, stack, onClose: () => flyout.close() })
    } else if (stack.type === 'adm') {
      navigate(`/monetization/one/sites/${stack.siteId}/stacks/${stack.id}`)
    }
  }

  const handleViewClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()

    flyout.open(
      ViewStackFlyout,
      { color: Colors.Ming, closeTrackingLabel: `${VIEW_STACK_TRACKING_LABEL}-close` },
      {
        site,
        stack: stack as PbsStack,
        onEditStack: handleEditClick,
      }
    )
  }

  const performArchive = async () => {
    await onStackArchive(stack, concurrentStack)
    queryClient.invalidateQueries({ queryKey: [...STACKS_QUERY_KEYS, { siteId: site.id }] })
  }

  const performUnarchive = async () => {
    try {
      await bulkUpdateStacks([toBulkUpdate({ ...stack, status: 'to-deploy', priority: maxPriority + 1 })])
      toast.success(`${stack.name} has been unarchived.`)
    } catch (_e) {
      toast.alert('An error occured while unarchiving this stack.')
    }

    queryClient.invalidateQueries({ queryKey: [...STACKS_QUERY_KEYS, { siteId: site.id }] })
  }

  const handleArchiveVersionClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isUpdatePending) return

    modal.open(ConfirmationModal, {
      title: 'Confirmation',
      message: (
        <>
          <div>
            You are about to archive <strong>{stack.name}</strong> and <strong>all its versions</strong>.
          </div>
          <div>To continue to monetize the ad units, make sure to use them in another stack. </div>
        </>
      ),
      confirmVariant: 'negative',
      trackingLabel: ARCHIVE_STACK_TRACKING_LABEL,
      onCancel: () => modal.close(),
      onConfirm: async () => {
        modal.close()
        await performArchive()
      },
    })
  }

  const handleUnarchiveVersionClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isUpdatePending) return

    modal.open(UnarchiveStackModal, {
      stackName: stack.name,
      onUnarchive: async () => {
        modal.close()
        await performUnarchive()
      },
    })
  }

  // A stack with a newer version beside it has no line under its first cells, so the two rows of the group read
  // as one. `!` because `TableCell` is Emotion, whose unlayered border wins over a utility.
  const cellClassName = cn(concurrentStack && concurrentStack.version > stack.version && 'border-b-0!')

  return (
    <TableRow
      // `!` for the same reason: the row sets its own colour.
      className={cn(ROW_CELL_CLASSES, isArchived && 'text-ash!')}
      clickable={
        isArchived
          ? undefined
          : {
              onClick: handleViewClick,
              trackingLabel: VIEW_STACK_TRACKING_LABEL,
            }
      }
    >
      {isEditingPriorities && (
        <TableCell className={cellClassName}>
          {isFirst && !isStackNameReserved(stack.name) && !isArchived && (
            <>
              <Button
                variant={'tertiary'}
                iconName={'chevron_up'}
                onClick={(event) => {
                  event.stopPropagation()
                  updatePriority(stack.stackGroupId, -1)
                }}
                disabled={stack.priority === 1}
                trackingLabel={'adm-sites-stacks-priorities-increase-apply'}
              />
              <Button
                variant={'tertiary'}
                iconName={'chevron_down'}
                onClick={(event) => {
                  event.stopPropagation()
                  updatePriority(stack.stackGroupId, 1)
                }}
                disabled={stack.priority === maxPriority}
                trackingLabel={'adm-sites-stacks-priorities-decrease-apply'}
              />
            </>
          )}
        </TableCell>
      )}
      <TableCell className={cn('w-8', cellClassName)}>{priority}</TableCell>
      <TableCell className={cellClassName}>
        <div className={'font-bold'}>{isFirst ? stack.name : ''}</div>
      </TableCell>
      <TableCell>
        <div className={'flex items-center gap-0.5'}>
          v{stack.version}
          {!isArchived && noBiddersMapped && <IconWithTooltip title={'No bidders mapped'} fill={Colors.Warning} width="1.25rem" height="1.25rem" name="warning" />}
        </div>
      </TableCell>
      <TableCell>
        <div className={'flex items-center gap-0.5'}>
          <StackStatus stack={stack} />
          {stack.ratio > 0 && stack.ratio < 1 ? `(${(stack.ratio * 100).toFixed(0)}%)` : ''}
        </div>
      </TableCell>
      <TableCell>{contexts.find((c) => c.id === stack.configuration.contextId)?.name || 'default'}</TableCell>
      <TableCell>
        <div className={'flex flex-row gap-2'}>
          {isPbsStack(stack) && (
            <>
              <Qualifier iconName="header_bidding" tooltipText={bidderRuleName || ''} active={!!bidderRuleName} enabled />
              <Qualifier iconName={'floor'} tooltipText={`Floor price ${stack.configuration.floorRuleConfig ? 'allowed' : 'disabled'}`} active={!!stack.configuration.floorRuleConfig} enabled />
            </>
          )}
          {isAdmStack(stack) && (
            <>
              <Qualifier iconName={'header_bidding'} tooltipText={bidderRuleName || ''} active={!!stack.configuration.headerBiddingEnabled} enabled />
              <Qualifier
                iconName={'loading'}
                tooltipText={`Lazy loading ${stack.configuration.lazyLoadingRuleConfig?.length ? 'allowed' : 'disabled'}`}
                active={!!stack.configuration.lazyLoadingRuleConfig?.length}
                enabled
              />
              <Qualifier iconName={'floor'} tooltipText={`Floor price ${stack.configuration.floorRuleConfig ? 'allowed' : 'disabled'}`} active={!!stack.configuration.floorRuleConfig} enabled />
              {user.hasFeature('admRefresh') && (
                <Qualifier
                  iconName={'refresh'}
                  tooltipText={`Refresh ${stack.configuration.refreshRuleConfig?.length ? 'allowed' : 'disabled'}`}
                  active={!!stack.configuration.refreshRuleConfig?.length}
                  enabled
                />
              )}
            </>
          )}
        </div>
      </TableCell>
      {/* One's column: whether the stack calls Prebid Server, through its legacy seats or its server-side variant. */}
      {product === 'adm' && (
        <TableCell>
          {isAdmStack(stack) && (
            <div className={'flex flex-row gap-2'}>
              {serversideQualifiersOf(serversideRoutes).map(({ key, iconName, tooltipText, active }) => (
                <Qualifier key={key} iconName={iconName} tooltipText={tooltipText} active={active} enabled />
              ))}
            </div>
          )}
        </TableCell>
      )}
      {showStackConfigColumn && <ConfigButtonsCell stackConfigUrl={getStackConfigUrlPrefix()} stack={stack} />}
      <ActionsTableCell>
        <ActionButtons>
          {/* Deploying, versioning and re-dialling a ratio all change what is served, so they are the owner's
              and the API refuses them to anyone else. Disabled with the reason rather than hidden: a reader
              who cannot do it still needs to see that the capability exists in order to go and ask. */}
          {stack.isOutOfSync && isLast && (
            <Tooltip title={canEdit ? 'Create updated version' : disabledReason}>
              <Button
                iconName="sync"
                variant="tertiary"
                disabled={!canEdit || (stack.status === 'live' && stack.ratio < 1) || isEditingPriorities}
                onClick={handleCreateUpdatedVersion}
                trackingLabel={DEPLOY_STACK_TRACKING_LABEL}
              />
            </Tooltip>
          )}
          {!stack.isOutOfSync && stack.status === 'to-deploy' && (
            <>
              <Tooltip title={canEdit ? 'Deploy' : disabledReason}>
                <Button iconName="send" variant="tertiary" disabled={!canEdit || isUpdatePending || isEditingPriorities} onClick={handleDeployClick} trackingLabel={DEPLOY_STACK_TRACKING_LABEL} />
              </Tooltip>
              {/* <Tooltip title="A/B deploy">
                <Button iconName="ab_deploy" variant="tertiary" disabled={isUpdatePending} onClick={handleDeployClick} trackingLabel={AB_DEPLOY_STACK_TRACKING_LABEL} />
              </Tooltip> */}
            </>
          )}
          {stack.status === 'live' && stack.ratio > 0 && stack.ratio < 1 && (
            <Tooltip title={canEdit ? 'Edit current deployment ratio' : disabledReason}>
              <Button
                iconName="tune"
                variant="tertiary"
                disabled={!canEdit || isUpdatePending || isEditingPriorities}
                onClick={handleDeployClick}
                trackingLabel={EDIT_AB_DEPLOY_RATIO_TRACKING_LABEL}
              />
            </Tooltip>
          )}
          {!isArchived && (
            <>
              <Tooltip title={canEdit ? 'Edit' : disabledReason}>
                <Button
                  iconName="edit"
                  variant="tertiary"
                  disabled={
                    !canEdit ||
                    isUpdatePending ||
                    (stack.status === 'live' && concurrentStack !== undefined && stack.version < concurrentStack.version) ||
                    stack.isOutOfSync ||
                    isEditingPriorities ||
                    (stack.ratio < 1 && stack.status === 'live')
                  }
                  onClick={handleEditClick}
                  trackingLabel={EDIT_STACK_TRACKING_LABEL}
                />
              </Tooltip>

              <Tooltip title="View">
                <Button iconName="view" variant="tertiary" onClick={handleViewClick} trackingLabel={VIEW_STACK_TRACKING_LABEL} />
              </Tooltip>

              {(concurrentStack === undefined || concurrentStack.version < stack.version) && (
                <Tooltip reposition title={!canEdit ? disabledReason : isStackNameReserved(stack.name) ? 'Default stack cannot be archived' : 'Archive'}>
                  <Button
                    iconName="archive"
                    variant="tertiary"
                    disabled={!canEdit || isUpdatePending || isStackNameReserved(stack.name) || isEditingPriorities}
                    onClick={handleArchiveVersionClick}
                    trackingLabel={ARCHIVE_STACK_TRACKING_LABEL}
                  />
                </Tooltip>
              )}
            </>
          )}
          {isArchived && (
            <Tooltip reposition title={canEdit ? 'Unarchive' : disabledReason}>
              <Button
                iconName="unarchive"
                variant="tertiary"
                disabled={!canEdit || isUpdatePending || isEditingPriorities}
                onClick={handleUnarchiveVersionClick}
                trackingLabel={UNARCHIVE_STACK_TRACKING_LABEL}
              />
            </Tooltip>
          )}
        </ActionButtons>
      </ActionsTableCell>
    </TableRow>
  )
}
